import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "~/common/mui";
import { Col, Row } from "~/common/components/Layout";
import { ParamComponent } from "~/common/components/ParamsGrid";
import { type CaseDataObject, CaseStatus } from "~/features/orders/orderDetails/components/ServiceCases/types";
import { format } from "~/common/utils/ApiHelper";
import Tag from "~/common/components/Tag/Tag";
import { getTagVariant } from "~/features/orders/orderDetails/components/ServiceCases/caseStatusPresentation";
import CloseIcon from "@mui/icons-material/Close";

interface AddCommentModalProps {
  onClose: () => void;
  onSend: (code: string, message: string) => void;
  caseData?: CaseDataObject;
}

interface FormData {
  feedbackMessage: string;
}

const AddCommentModal = ({ caseData, onSend, onClose }: AddCommentModalProps) => {
  const { t } = useTranslation();
  const { control, handleSubmit, formState } = useForm<FormData>({ defaultValues: { feedbackMessage: "" } });

  const onSubmit = async (data: FormData) => {
    if (caseData) {
      onSend(caseData.code, data.feedbackMessage);
    }
    onClose();
  };

  const FooterButton = (
    <Stack direction={"row"} gap={1}>
      <Button onClick={onClose} variant="text">
        {t("page.orderQuoteSettings.modal.button.cancel")}
      </Button>
      <Button disabled={!formState.isValid} onClick={() => handleSubmit(onSubmit)()} fullWidth>
        {t("request.information.send")}
      </Button>
    </Stack>
  );

  const formatTime = (timeStamp: string) => {
    return format(new Date(timeStamp), "DD.MM.YYYY");
  };

  if (!caseData) {
    return null;
  }

  return (
    <Dialog open={true} maxWidth="desktop-s">
      <DialogHeader onClose={onClose}>
        <DialogTitle variant="headlineSmall">{t("serviceCases.list.addComment.title")}</DialogTitle>
      </DialogHeader>
      <DialogContent>
        <form>
          <Stack gap={4} paddingBottom={2}>
            <Stack gap={2}>
              <Typography variant="titleMedium" component={"b"}>
                {t("addComment.header")}
              </Typography>
              <Row>
                <Col size={4}>
                  <ParamComponent label={t("addComment.caseNumber")} value={caseData?.sfCaseNumber} />
                </Col>
                <Col size={6}>
                  <ParamComponent label={t("addComment.subject")} value={caseData?.reasonOfRequest} />
                </Col>
              </Row>
              <Row>
                <Col size={4}>
                  <ParamComponent
                    label={t("addComment.caseStatus")}
                    value={<Tag variant={getTagVariant(caseData.status)}> {t(CaseStatus[caseData.status])}</Tag>}
                  />
                </Col>
                <Col size={4}>
                  <ParamComponent label={t("addComment.createdAt")} value={formatTime(caseData.createdAt)} />
                </Col>
                <Col size={4}>
                  <ParamComponent label={t("addComment.updatedAt")} value={formatTime(caseData.updatedAt)} />
                </Col>
              </Row>
            </Stack>
          </Stack>
          <Controller
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                variant={"outlined"}
                inputRef={field.ref}
                label={t("addComment.placeholder") + "*"}
                helperText={t("order.information.header.helper")}
                multiline
                rows={2}
                fullWidth
                error={!!error}
                slotProps={{
                  input: {
                    endAdornment: (
                      <IconButton onClick={() => field.onChange("")}>
                        <CloseIcon />
                      </IconButton>
                    ),
                  },
                }}
              />
            )}
            name={"feedbackMessage"}
            control={control}
            rules={{ required: true, maxLength: 512 }}
            defaultValue={""}
          />
        </form>
      </DialogContent>
      <DialogActions>{FooterButton}</DialogActions>
    </Dialog>
  );
};

export default AddCommentModal;
